/**
 * 旅行 (Trip) 用 Zustand ストア
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { create } from 'zustand'

import {
  deleteTrip as deleteTripApi,
  generateTripStory as generateTripStoryApi,
  getConquestMap,
  getTrip,
  getTripRoute,
  getTrips,
  getTripTimeline,
  updateTrip as updateTripApi,
} from '@/api/services/trips'
import type { ConquestMap, Trip, TripDetail, TripRoute, TripTimeline, TripUpdatePayload } from '@/api/types/trip'

type TripState = {
  trips: Trip[]
  isTripsLoading: boolean

  detail: TripDetail | null
  timeline: TripTimeline | null
  route: TripRoute | null
  isDetailLoading: boolean
  isStoryGenerating: boolean

  conquest: ConquestMap | null
  isConquestLoading: boolean

  error: string | null

  fetchTrips: () => Promise<void>
  fetchTripDetail: (tripId: number) => Promise<void>
  updateTrip: (tripId: number, payload: TripUpdatePayload) => Promise<void>
  deleteTrip: (tripId: number) => Promise<void>
  generateStory: (tripId: number) => Promise<void>
  fetchConquest: () => Promise<void>
  clearDetail: () => void
  clearError: () => void
}

const initialState = {
  trips: [],
  isTripsLoading: false,
  detail: null,
  timeline: null,
  route: null,
  isDetailLoading: false,
  isStoryGenerating: false,
  conquest: null,
  isConquestLoading: false,
  error: null,
}

const toMessage = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback)

export const useTripStore = create<TripState>((set, get) => ({
  ...initialState,

  fetchTrips: async () => {
    set({ isTripsLoading: true, error: null })
    try {
      set({ trips: await getTrips() })
    } catch (error) {
      set({ error: toMessage(error, '旅行の取得に失敗しました') })
    } finally {
      set({ isTripsLoading: false })
    }
  },

  fetchTripDetail: async (tripId) => {
    set({ isDetailLoading: true, error: null })
    try {
      // 詳細・タイムライン・軌跡は同時に必要になるためまとめて取る
      const [detail, timeline, route] = await Promise.all([
        getTrip(tripId),
        getTripTimeline(tripId),
        getTripRoute(tripId),
      ])
      set({ detail, timeline, route })
    } catch (error) {
      set({ error: toMessage(error, '旅の情報の取得に失敗しました') })
    } finally {
      set({ isDetailLoading: false })
    }
  },

  updateTrip: async (tripId, payload) => {
    // 先に画面へ反映し、失敗したら元に戻す
    const before = get()
    set((state) => ({
      trips: state.trips.map((t) => (t.id === tripId ? { ...t, ...payload } : t)),
      detail: state.detail?.id === tripId ? { ...state.detail, ...payload } : state.detail,
    }))

    try {
      const updated = await updateTripApi(tripId, payload)
      set((state) => ({
        trips: state.trips.map((t) => (t.id === tripId ? updated : t)),
        detail: state.detail?.id === tripId ? { ...state.detail, ...updated } : state.detail,
      }))
    } catch (error) {
      set({ trips: before.trips, detail: before.detail, error: toMessage(error, '旅の更新に失敗しました') })
    }
  },

  deleteTrip: async (tripId) => {
    try {
      await deleteTripApi(tripId)
      set((state) => ({
        trips: state.trips.filter((t) => t.id !== tripId),
        detail: state.detail?.id === tripId ? null : state.detail,
      }))
    } catch (error) {
      set({ error: toMessage(error, '旅の削除に失敗しました') })
    }
  },

  generateStory: async (tripId) => {
    set({ isStoryGenerating: true, error: null })
    try {
      const result = await generateTripStoryApi(tripId)
      set((state) => ({
        detail: state.detail
          ? { ...state.detail, ai_story: result.ai_story, ai_story_at: result.ai_story_at, has_story: true }
          : state.detail,
        trips: state.trips.map((t) => (t.id === tripId ? { ...t, has_story: true } : t)),
      }))
    } catch (error) {
      set({ error: toMessage(error, '紀行文の生成に失敗しました') })
    } finally {
      set({ isStoryGenerating: false })
    }
  },

  fetchConquest: async () => {
    set({ isConquestLoading: true, error: null })
    try {
      set({ conquest: await getConquestMap() })
    } catch (error) {
      set({ error: toMessage(error, '制覇マップの取得に失敗しました') })
    } finally {
      set({ isConquestLoading: false })
    }
  },

  clearDetail: () => set({ detail: null, timeline: null, route: null }),
  clearError: () => set({ error: null }),
}))
